import { useSnackbar } from 'notistack5';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { GeneralEnquete } from 'src/@types/generalEnquete';
import { useEnqueteAnswerInputApi } from 'src/api/useEnqueteAnswerInputApi';
import { FORM_TYPE, REQUEST_STATUS, SURVEY_TYPE } from 'src/constants';
import useEnqueteAnswer from 'src/features/general/enquete-answer/hooks/useEnqueteAnswer';
import {
  SurveyDetailEntity,
  resetEnqueteData,
  setAfterRequestTerm,
  setBeforeRequestTerm,
  setEnqueteAnswerOpen,
  setRespondentRequestData,
  setRespondentTargetData,
  setSurveyDetailAction5Data,
  setSurveyDetailData,
  setUploadFileData,
} from 'src/features/general/enquete-answer/store/enqueteAnswerSlice';
import { setInitGeneralAction5Questions, setInitGeneralQuestions } from 'src/features/general/enquete-answer/store/enqueteGeneralAnswerSlice';
import { usePermission } from 'src/hooks/usePermission';
import { useAppDispatch, useAppSelector } from 'src/redux/store';
import { getRequestStatus } from 'src/utils/getRequestStatus';

const useEnqueteAnswerInputLayout = () => {
  const respondentTargetId = useParams().id;
  const confirmRequestId: string | null = new URLSearchParams(useLocation().search).get('confirmRequestId');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { getRespondentTarget, getRespondentRequest, getSurveyDetail, getUploadFile, getSurveyAction5Detail, getUploadAction5File } =
    useEnqueteAnswerInputApi();
  const { fetchAnswer, fetchConfirmRequest, fetchConfirmHistories, fetchAnswerAction5 } = useEnqueteAnswer();

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const respondentRequestData = useAppSelector((state) => state.enqueteAnswer.respondentRequestData);
  const surveyDetailData = useAppSelector((state) => state.enqueteAnswer.surveyDetailData);

  const { isAdmin, isSurveyOwner } = usePermission();

  const isGeneral = surveyDetailData.surveyHeader?.type === SURVEY_TYPE.GENERAL;
  const isSeries = surveyDetailData.surveyHeader?.formType === FORM_TYPE.SERIES;
  const isOverview = surveyDetailData.surveyHeader?.formType === FORM_TYPE.OVERVIEW;
  const isComplia = surveyDetailData.surveyHeader?.formType === FORM_TYPE.COMPLIA;

  // todo: 依頼ステータスによる管理が複雑、as is to be を整理してグローバルステートを見直した方がよい
  const requestStatus = getRequestStatus(respondentRequestData.openedAt, respondentRequestData.closedAt);

  // 依頼前 条件：　bonsai-adminでないこと、アンケート管理者でないこと、依頼前であること
  dispatch(setBeforeRequestTerm(!isAdmin && !isSurveyOwner && (requestStatus === undefined || requestStatus === REQUEST_STATUS.BEFORE_REQUEST)));
  // 実施中　条件：　汎用・系列別・概況表 コンプラ はステータスを参照し、他だった場合は権限とステータスを参照していいます
  isGeneral || isSeries || isOverview || isComplia
    ? dispatch(setEnqueteAnswerOpen(requestStatus === REQUEST_STATUS.IN_PROGRESS))
    : dispatch(setEnqueteAnswerOpen(isAdmin || isSurveyOwner || requestStatus === REQUEST_STATUS.IN_PROGRESS));
  // 完了 条件：　bonsai-adminでないこと、アンケート管理者でないこと、完了であること
  dispatch(setAfterRequestTerm(!isAdmin && !isSurveyOwner && requestStatus === REQUEST_STATUS.COMPLETE));

  const fetchRespondentTarget = async (respondentTargetId: string): Promise<void> => {
    const response = await getRespondentTarget(respondentTargetId);
    if (!response) throw new Error();
    dispatch(setRespondentTargetData(response));
  };

  const fetchRespondentRequest = async (respondentTargetId: string): Promise<void> => {
    const response = await getRespondentRequest(respondentTargetId);
    if (!response) throw new Error();
    dispatch(setRespondentRequestData(response));
  };

  const fetchSurveyDetail = async (respondentTargetId: string): Promise<SurveyDetailEntity | undefined> => {
    const response = await getSurveyDetail(respondentTargetId);
    if (!response) throw new Error();
    dispatch(setSurveyDetailData(response));
    return response;
  };

  //アクション⑤アンケート詳細取得
  const fetchSurveyAction5Detail = async (surveyDetailId: string, respondentTargetId: string): Promise<SurveyDetailEntity | undefined> => {
    const response = await getSurveyAction5Detail(surveyDetailId, respondentTargetId);
    //データが存在しない場合、後続処理を停止する
    if (!response) return;
    //アクション⑤アンケート詳細更新
    dispatch(setSurveyDetailAction5Data(response));
    return response;
  };

  const setAnswerData = async (respondentTargetId: string) => {
    let formData: GeneralEnquete;
    const uploadFiles = [];
    const surveyDetailData = await fetchSurveyDetail(respondentTargetId);
    const answerData = await fetchAnswer(respondentTargetId);
    if (answerData?.id) {
      await fetchConfirmHistories(answerData.id);
    }
    if (answerData?.id && surveyDetailData?.id && surveyDetailData.surveyHeader?.formType === FORM_TYPE.COMPLIA) {
      const result = await getUploadFile(surveyDetailData.id, answerData.id);
      uploadFiles.push(...result);
    }

    // 汎用アンケートのためのデータを作成する
    if (answerData) {
      formData = answerData.answerData.answerInputData.tabData[0] as GeneralEnquete;
    } else {
      if (!surveyDetailData) throw new Error();
      formData = surveyDetailData.surveyFormData.formData.tabData[0] as GeneralEnquete;
    }
    if (formData.questionsJpn && formData.questionsEng) {
      if (i18n.language === 'ja') {
        dispatch(setInitGeneralQuestions(formData.questionsJpn));
      } else {
        dispatch(setInitGeneralQuestions(formData.questionsEng));
      }
    }

    // アクション⑤のデータ取得
    if (surveyDetailData?.id && surveyDetailData.surveyHeader?.formType === FORM_TYPE.COMPLIA) {
      let action5FormData: GeneralEnquete;
      //アクション⑤詳細データ取得
      let surveyAction5DetailData = await fetchSurveyAction5Detail(surveyDetailData.id, respondentTargetId);
      //アクション⑤回答のデータ取得
      const answerDataAction5 = await fetchAnswerAction5(respondentTargetId);
      surveyAction5DetailData &&
        (surveyAction5DetailData = {
          ...surveyAction5DetailData,
          //アクション⑤詳細のヘーダ設定
          surveyHeader: surveyDetailData.surveyHeader,
          //アクション⑤詳細ヘーダIdのヘーダ設定
          surveyHeaderId: surveyDetailData.surveyHeaderId,
        });

      //アクション⑤のファイル取得
      if (answerDataAction5?.id && surveyAction5DetailData?.id) {
        //ファイル取得
        const result: any[] = await getUploadAction5File(surveyAction5DetailData.id, answerDataAction5.id);
        uploadFiles.push(
          ...result.map((item) => {
            return {
              ...item,
              surveyDetailId: item.surveyDetailAction5Id,
              answerId: item.answerAction5Id,
            };
          }),
        );
      }

      // 汎用アンケートのためのデータを作成する
      if (answerDataAction5) {
        action5FormData = answerDataAction5.answerData.answerInputData.tabData[0] as GeneralEnquete;
      } else {
        if (!surveyAction5DetailData) return;
        action5FormData = surveyAction5DetailData.surveyFormData.formData.tabData[0] as GeneralEnquete;
      }
      //環境言語に応じてフォームデータを設定する
      if (action5FormData.questionsJpn && action5FormData.questionsEng) {
        if (i18n.language === 'ja') {
          dispatch(setInitGeneralAction5Questions(action5FormData.questionsJpn));
        } else {
          dispatch(setInitGeneralAction5Questions(action5FormData.questionsEng));
        }
      }
    }
    dispatch(setUploadFileData(uploadFiles));
  };

  const fetchEnqueteBaseData = async () => {
    if (!respondentTargetId) {
      // todo: snackbar ではなく画面全体でエラーを表示した方が望ましい、404ページとか
      enqueueSnackbar(t('enqueteAnswerInput.message.exceptionRespondentTarget'), { variant: 'error' });
      navigate('../../enquete-answer', { replace: true });
      return;
    }

    setIsLoading(true);
    try {
      await Promise.all([fetchRespondentTarget(respondentTargetId), fetchRespondentRequest(respondentTargetId), setAnswerData(respondentTargetId)]);
      // 確認依頼から遷移していれば confirmRequestId を元に ConfirmRequest を取得
      if (!confirmRequestId) return;

      await fetchConfirmRequest(confirmRequestId);
    } catch (error) {
      enqueueSnackbar(t('enqueteAnswerInput.message.exceptionRespondentTarget'), { variant: 'error' });
      navigate('../../enquete-answer', { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEnqueteBaseData();
    return () => {
      dispatch(resetEnqueteData());
      dispatch(setInitGeneralQuestions([]));
      dispatch(setInitGeneralAction5Questions([]));
    };
  }, []); //eslint-disable-line

  return {
    isLoading,
  };
};

export default useEnqueteAnswerInputLayout;
